import React, { useState, useRef, useEffect } from "react";
import AudioRecordModal from "../AudioRecordModal/AudioRecordModal";
import "./ConversationBar.css";
import { translations } from "../../utils/translations";

const ConversationBar = ({
  onNewUserMessage,
  onBotResponse,
  onCancel,
  replikerId,
  conversationId,
  showAttachments = true,
  isLoading,
  selectedFile,
  setSelectedFile,
  placeholder,
  value,
  onChange,
}) => {
  const [internalInput, setInternalInput] = useState("");
  const input = value !== undefined ? value : internalInput;
  
  const handleInputChange = (e) => {
    const newVal = e.target.value;
    if (onChange) {
      onChange(newVal);
    } else {
      setInternalInput(newVal);
    }
  };
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  const currentLang = localStorage.getItem("language") || "es";
  const t =
    translations[currentLang]?.chatComponent ||
    translations["es"].chatComponent;

  useEffect(() => {
    if (selectedFile && selectedFile.type.startsWith("image/")) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);

      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [selectedFile]);

  useEffect(() => {
    // Auto-resize textarea
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [input]);

  useEffect(() => {
    // Solo enfocar en el input en pantallas grandes (no móviles)
    if (!isLoading && inputRef.current && window.innerWidth > 768) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (onCancel) {
      onCancel();
    }
  };

  const handleSend = async () => {
    if (isLoading || (!input.trim() && !selectedFile)) return;

    const userMessage = {
      sender: "user",
      text: input,
      file: selectedFile,
    };

    onNewUserMessage(userMessage);

    if (onChange) {
      onChange("");
    } else {
      setInternalInput("");
    }
    setSelectedFile(null);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault(); // Prevent new line on Enter
      handleSend();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleAudioSend = (audioFile) => {
    setSelectedFile(audioFile);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
  };

  return (
    <>
      <AudioRecordModal
        isOpen={showAudioModal}
        onClose={() => setShowAudioModal(false)}
        onSend={handleAudioSend}
      />
      <div className="conversation-bar-wrapper">
        {selectedFile && (
          <div className="file-preview">
            {previewUrl && (
              <img src={previewUrl} alt="Preview" className="image-preview" />
            )}
            <span>{selectedFile.name}</span>
            <button onClick={removeSelectedFile}>×</button>
          </div>
        )}
        <div
          className={`conversation-bar-container ${isLoading ? "disabled" : ""
            }`}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder={
              isLoading ? t.placeholderThinking : (placeholder || t.placeholderDefault)
            }
            disabled={isLoading}
            rows="1"
            style={{
              maxHeight: "150px",
              resize: "none",
              overflowY: "auto",
              width: "100%",
            }}
          />

          <div className="conversation-bar-footer">
            <div className="footer-left">
              {showAttachments && (
                <>
                  <button
                    className="attachment-option"
                    onClick={openFilePicker}
                    disabled={isLoading}
                    title={t.attachFileTitle}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="24px"
                      viewBox="0 -960 960 960"
                      width="24px"
                      fill="#e3e3e3"
                    >
                      <path d="M720-330q0 104-73 177T470-80q-104 0-177-73t-73-177v-370q0-75 52.5-127.5T400-880q75 0 127.5 52.5T580-700v350q0 46-32 78t-78 32q-46 0-78-32t-32-78v-370h80v370q0 13 8.5 21.5T470-320q13 0 21.5-8.5T500-350v-350q-1-42-29.5-71T400-800q-42 0-71 29t-29 71v370q-1 71 49 120.5T470-160q70 0 119-49.5T640-330v-390h80v390Z" />
                    </svg>
                  </button>
                  <button
                    className="attachment-option"
                    onClick={() => setShowAudioModal(true)}
                    disabled={isLoading}
                    title={t.recordAudioTitle}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="24px"
                      viewBox="0 -960 960 960"
                      width="24px"
                      fill="#e3e3e3"
                    >
                      <path d="M480-400q-50 0-85-35t-35-85v-240q0-50 35-85t85-35q50 0 85 35t35 85v240q0 50-35 85t-85 35Zm0-240Zm-40 520v-123q-104-14-172-93t-68-184h80q0 83 58.5 141.5T480-320q83 0 141.5-58.5T680-520h80q0 105-68 184t-172 93v123h-80Zm40-360q17 0 28.5-11.5T520-520v-240q0-17-11.5-28.5T480-800q-17 0-28.5 11.5T440-760v240q0 17 11.5 28.5T480-480Z" />
                    </svg>
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept=".pdf,.csv,.xls,.xlsx,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                </>
              )}
            </div>

            <div className="footer-right">
              {isLoading ? (
                <button onClick={handleStop} className="stop-button">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="24" height="24" fill="white" />
                  </svg>
                </button>
              ) : (
                <button onClick={handleSend} className="send-button">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z"
                      fill="black"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConversationBar;
